import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Wrench, Phone, Mail, MapPin, MessageCircle } from 'lucide-react';

const TALLERES = {
  costa: {
    nombre: 'Suc. Costa',
    direccion: 'Av. Giannattasio Km 21.500, Ciudad de la Costa',
    telefono: '092 487 711',
    email: 'amaya.peugeot@gmail.com',
    wa: '59892487711'
  },
  propios: {
    nombre: 'Suc. Propios',
    direccion: 'Bvar. Batlle y Ordóñez 3848',
    telefono: '092 931 302',
    email: 'amaya.motors@amaya.com.uy',
    wa: '59892931302'
  },
  nuevocentro: {
    nombre: 'Suc. Nuevocentro',
    direccion: 'Luis Alberto de Herrera 3428',
    telefono: '092 394 511',
    email: 'amaya.byd@gmail.com',
    wa: '59892394511'
  }
};

const AgendaService = () => {
  const [searchParams] = useSearchParams();
  const taller = TALLERES[searchParams.get('taller')] || null;

  return (
    <div className="min-h-screen bg-[#050509] text-white flex flex-col">
      {/* Fondo radial */}
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 25%, rgba(59,130,246,0.07) 0%, transparent 60%)' }} />

      {/* Navbar mínima */}
      <nav className="relative z-10 px-6 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Link to="/" className="flex items-center gap-2 text-sm transition-colors" style={{ color: 'rgba(255,255,255,0.4)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'white'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>
        <img
          src="https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/7q1rfiej_Logo_Amaya_Blanco_para_fondo_oscuro.png"
          alt="Amaya Motors"
          className="h-8 w-auto"
          style={{ opacity: 0.65 }}
        />
      </nav>

      {/* Contenido principal */}
      <main className="relative z-10 flex-grow flex items-center justify-center px-6 py-16">
        <div className="max-w-md w-full">

          {/* Ícono animado */}
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: 'spring', stiffness: 120 }}
            className="flex justify-center mb-10"
          >
            <div className="relative">
              <motion.div
                className="absolute rounded-full"
                style={{ inset: '-12px', border: '1px solid rgba(59,130,246,0.15)' }}
                animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute rounded-full"
                style={{ inset: '-24px', border: '1px solid rgba(59,130,246,0.07)' }}
                animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
              />
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}
              >
                <Wrench className="w-10 h-10 text-blue-400" />
              </div>
              <motion.div
                className="absolute -top-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)' }}
                animate={{ y: [-3, 3, -3] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Calendar className="w-4 h-4 text-blue-300" />
              </motion.div>
            </div>
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="flex justify-center mb-4"
          >
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest"
              style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: 'rgba(96,165,250,1)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              Próximamente
            </span>
          </motion.div>

          {/* Título */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="text-3xl md:text-4xl font-black text-center mb-3"
          >
            Agenda de Service
          </motion.h1>

          {/* Descripción */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="text-center leading-relaxed mb-10"
            style={{ color: 'rgba(255,255,255,0.45)' }}
          >
            La agenda online estará disponible en breve.
            <br className="hidden sm:block" />
            Mientras tanto, contactá directamente al taller:
          </motion.p>

          {/* Tarjeta del taller */}
          {taller ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, type: 'spring', stiffness: 100 }}
              className="rounded-2xl p-6 mb-8"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              {/* Header del taller */}
              <div className="flex items-center gap-3 mb-5 pb-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}
                >
                  <Wrench className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider mb-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>Seleccionaste</p>
                  <h2 className="font-bold text-white">{taller.nombre}</h2>
                </div>
              </div>

              {/* Info de contacto */}
              <div className="space-y-3 mb-6">
                <a
                  href={`https://maps.google.com?q=${encodeURIComponent(taller.direccion + ', Uruguay')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 transition-colors"
                  style={{ color: 'rgba(255,255,255,0.55)' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}
                >
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-blue-400" />
                  <span className="text-sm">{taller.direccion}</span>
                </a>
                <a
                  href={`tel:${taller.telefono.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 transition-colors"
                  style={{ color: 'rgba(255,255,255,0.55)' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}
                >
                  <Phone className="w-4 h-4 shrink-0 text-blue-400" />
                  <span className="text-sm">{taller.telefono}</span>
                </a>
                <a
                  href={`mailto:${taller.email}`}
                  className="flex items-center gap-3 transition-colors"
                  style={{ color: 'rgba(255,255,255,0.55)' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}
                >
                  <Mail className="w-4 h-4 shrink-0 text-blue-400" />
                  <span className="text-sm lowercase">{taller.email}</span>
                </a>
              </div>

              {/* Botones de acción */}
              <div className="grid grid-cols-2 gap-3">
                <a
                  href={`https://wa.me/${taller.wa}?text=${encodeURIComponent('Hola Amaya, quisiera agendar un service.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
                <a
                  href={`tel:${taller.telefono.replace(/\s/g, '')}`}
                  className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}
                >
                  <Phone className="w-4 h-4" />
                  Llamar
                </a>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="rounded-2xl p-8 mb-8 text-center"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Seleccioná un taller para ver sus datos de contacto.
              </p>
              <Link
                to="/#service"
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                Ver talleres →
              </Link>
            </motion.div>
          )}

          {/* Volver */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center"
          >
            <Link
              to="/#service"
              className="text-xs transition-colors"
              style={{ color: 'rgba(255,255,255,0.25)' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}
            >
              ← Volver a los talleres
            </Link>
          </motion.div>

        </div>
      </main>
    </div>
  );
};

export default AgendaService;
